/**
 * Typed client for COI sharing: share links, public shared views,
 * co-signs, and forks.
 *
 * Contract:
 *  - POST   /v1/cois/{id}/share        -> {share_url, rotated} (auth: author/staff)
 *  - GET    /v1/cois/{id}/share        -> {share_url | null}   (auth: author/staff)
 *  - DELETE /v1/cois/{id}/share        -> 204                  (auth: author/staff)
 *  - GET    /v1/shared/coi/{token}     -> SharedCoi            (PUBLIC)
 *  - POST   /v1/shared/coi/{token}/cosign -> {cosign_count}    (PUBLIC)
 *  - POST   /v1/shared/coi/{token}/fork   -> ForkedCoiDraft    (auth)
 *
 * Defensive posture matches lib/sharing.ts: coerce, don't crash.
 */
import { toAppUrl } from './sharing';
import { ApiError, apiFetch } from './api';
import type { CoiSketchProvenance } from './coi';

export interface SharedCoi {
  token: string;
  coi_id: string;
  title: string | null;
  narrative: string;
  interests: string | null;
  polygon_geojson: Record<string, unknown>;
  /** [west, south, east, north] in EPSG:4326, or null for narrative-only COIs. */
  bbox: [number, number, number, number] | null;
  status: string;
  cosign_count: number;
  fork_count: number;
  sketch_provenance: CoiSketchProvenance | null;
  author_display_name: string | null;
  created_at: string;
}

export interface ForkedCoiDraft {
  coi_id: string;
  title: string | null;
  narrative: string;
  interests: string | null;
  polygon_geojson: Record<string, unknown>;
  sketch_provenance: CoiSketchProvenance | null;
  parent_coi_id: string;
  created_at: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toBBox(value: unknown): [number, number, number, number] | null {
  if (
    Array.isArray(value) &&
    value.length === 4 &&
    value.every((v) => typeof v === 'number' && Number.isFinite(v))
  ) {
    return [value[0], value[1], value[2], value[3]];
  }
  return null;
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

function toSketchProvenance(value: unknown): CoiSketchProvenance | null {
  if (!isRecord(value)) return null;
  return {
    provider: toString(value.provider, 'unknown'),
    steps: toStringList(value.steps),
    warnings: toStringList(value.warnings),
    notes: toStringList(value.notes),
  };
}

function toSharedCoi(value: unknown): SharedCoi {
  const record = isRecord(value) ? value : {};
  return {
    token: toString(record.token, ''),
    coi_id: toString(record.coi_id, ''),
    title: toStringOrNull(record.title),
    narrative: toString(record.narrative, ''),
    interests: toStringOrNull(record.interests),
    polygon_geojson: isRecord(record.polygon_geojson) ? record.polygon_geojson : {},
    bbox: toBBox(record.bbox),
    status: toString(record.status, 'received'),
    cosign_count: toNumber(record.cosign_count, 0),
    fork_count: toNumber(record.fork_count, 0),
    sketch_provenance: toSketchProvenance(record.sketch_provenance),
    author_display_name:
      typeof record.author_display_name === 'string' ? record.author_display_name : null,
    created_at: toString(record.created_at, ''),
  };
}

function toForkedCoiDraft(value: unknown): ForkedCoiDraft {
  const record = isRecord(value) ? value : {};
  return {
    coi_id: toString(record.coi_id, ''),
    title: toStringOrNull(record.title),
    narrative: toString(record.narrative, ''),
    interests: toStringOrNull(record.interests),
    polygon_geojson: isRecord(record.polygon_geojson) ? record.polygon_geojson : {},
    sketch_provenance: toSketchProvenance(record.sketch_provenance),
    parent_coi_id: toString(record.parent_coi_id, ''),
    created_at: toString(record.created_at, ''),
  };
}

/** POST /v1/cois/{id}/share — create or rotate the share link. */
export async function shareCoi(coiId: string): Promise<{ share_url: string; rotated: boolean }> {
  const payload = await apiFetch<unknown>(`/v1/cois/${encodeURIComponent(coiId)}/share`, {
    method: 'POST',
  });
  const record = isRecord(payload) ? payload : {};
  return {
    share_url: toAppUrl(toString(record.share_url, '')),
    rotated: record.rotated === true,
  };
}

/** GET /v1/cois/{id}/share — current share link, if any. */
export async function getCoiShareInfo(coiId: string): Promise<{ share_url: string | null }> {
  const payload = await apiFetch<unknown>(`/v1/cois/${encodeURIComponent(coiId)}/share`);
  const record = isRecord(payload) ? payload : {};
  return {
    share_url: typeof record.share_url === 'string' ? toAppUrl(record.share_url) : null,
  };
}

/** DELETE /v1/cois/{id}/share — revoke the share link. */
export async function revokeCoiShare(coiId: string): Promise<void> {
  await apiFetch<unknown>(`/v1/cois/${encodeURIComponent(coiId)}/share`, {
    method: 'DELETE',
  });
}

/** GET /v1/shared/coi/{token} — public shared-COI view. */
export async function getSharedCoi(token: string): Promise<SharedCoi> {
  const payload = await apiFetch<unknown>(`/v1/shared/coi/${encodeURIComponent(token)}`);
  return toSharedCoi(payload);
}

/** Thrown when the contact already co-signed (HTTP 409). */
export class CoiCosignDuplicateError extends Error {
  constructor() {
    super('This contact has already co-signed this community.');
    this.name = 'CoiCosignDuplicateError';
  }
}

/**
 * POST /v1/shared/coi/{token}/cosign — co-sign a shared COI.
 * Returns the updated co-sign count.
 */
export async function cosignCoi(
  token: string,
  displayName: string,
  contact?: string,
): Promise<number> {
  try {
    const payload = await apiFetch<unknown>(
      `/v1/shared/coi/${encodeURIComponent(token)}/cosign`,
      {
        method: 'POST',
        body: JSON.stringify({
          display_name: displayName,
          ...(contact?.trim() ? { contact: contact.trim() } : {}),
        }),
      },
    );
    const record = isRecord(payload) ? payload : {};
    return toNumber(record.cosign_count, 0);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) throw new CoiCosignDuplicateError();
    throw error;
  }
}

/**
 * POST /v1/shared/coi/{token}/fork — copy a shared COI into your account as
 * an editable draft. Returns the draft fields so the caller can load them
 * into the /coi editor without a second fetch.
 */
export async function forkCoi(token: string): Promise<ForkedCoiDraft> {
  const payload = await apiFetch<unknown>(
    `/v1/shared/coi/${encodeURIComponent(token)}/fork`,
    { method: 'POST' },
  );
  return toForkedCoiDraft(payload);
}

/**
 * The share token inside a pasted COI share link ("https://…/p/coi/<token>")
 * or a bare token. Null when the text can't be a COI link.
 */
export function coiTokenFromLink(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const match = /\/p\/coi\/([A-Za-z0-9_-]+)/.exec(trimmed);
  if (match?.[1]) return match[1];
  if (/\/p\//.test(trimmed)) return null; // a plan link, not a community
  return /^[A-Za-z0-9_-]{8,}$/.test(trimmed) ? trimmed : null;
}
